import { fetchOneEntry, fetchEntries, type BuilderContent } from "@builder.io/sdk-vue";
import { CONTENT_TYPES } from '~~/shared/utils/contentTypes'
export interface BuilderIOData<T> {
  data?: T;
}

export interface BuilderIOEnrichedData<T> {
  value: {
    data: T;
  };
}

export interface BuilderIOFetchOptions {
  limit?: number;
  offset?: number;
  sort?: string | Record<string, 1 | -1>;
  fields?: string[];
  query?: Record<string, any>;
}

export class BuilderIOService {
  private publicApiKey: string;

  public constructor(publicApiKey: string) {
    this.publicApiKey = publicApiKey;
  }

  public getPublicApiKey() {
    return this.publicApiKey;
  }

  public async fetchOneModel<T>(model: string, urlPath: string, enrich?: boolean, options?: BuilderIOFetchOptions): Promise<BuilderIOData<T>> {
    const data = await fetchOneEntry({
      model: model,
      apiKey: this.publicApiKey,
      userAttributes: {
        urlPath: urlPath,
      },
      enrich: (enrich || false),
      options
    });

    if (data != null) {
      return data as BuilderIOData<T>;
    }
    else {
      return Promise.reject(`Data for model (${model}) was invalid`);
    }
  }

  public async fetchManyModels<T>(
    model: string,
    urlPath?: string,
    enrich?: boolean,
    options?: BuilderIOFetchOptions
  ): Promise<BuilderIOData<T>[]> {
    const fetchOptions: any = {
      model: model,
      apiKey: this.publicApiKey,
      ...(urlPath ? { userAttributes: { urlPath } } : {}),
      enrich: (enrich || false),
      options: {
        ...(options?.limit != null ? { limit: options.limit } : {}),
        ...(options?.offset != null ? { offset: options.offset } : {}),
        ...(options?.sort ? { sort: options.sort } : {}),
        ...(options?.fields ? { fields: options.fields } : {}),
      },
      ...(options?.query ? { query: options.query } : {}),
    };

    const data = await fetchEntries(fetchOptions);

    return Array.isArray(data) ? (data as BuilderIOData<T>[]) : [];
  }

  public async fetchTake5DailyDetails(slug: string) {
    const data = await fetchEntries({
      model: CONTENT_TYPES.TAKE_FIVE_DAILY,
      apiKey: this.publicApiKey,
      query: {
        'data.urlKey': slug
      },
    });

    if (data?.length) {
      return data[0]!.data;
    }

    return null;
  }

  public async fetchRelatedItems(tags: string[]) {
    if (!tags.length) return [];

    const data = await fetchEntries({
      model: CONTENT_TYPES.TAKE_FIVE_DAILY,
      apiKey: this.publicApiKey,
      options: {
        limit: 3,
      },
      query: {
        data: {
          entityType: CONTENT_TYPES.PODCASTS,
          taggings: {
            $elemMatch: {
              tag: { $in: tags },
            },
          },
        },
      },
    });

    return data || [];
  }

}

export default function () {
  const runtimeConfig = useRuntimeConfig();
  const builderIoKey = runtimeConfig.public.builderIoKey;
  if (typeof builderIoKey === "string") {
    return new BuilderIOService(builderIoKey);
  }
  else {
    throw new Error("Unable to build Builder IO service, public key is invalid.");
  }
}
